import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Bot, Calculator, BookOpen, FileText, Users, Home, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import logoSinconta from "@/assets/logo-sinconta.png";
import { Badge } from "@/components/ui/badge";

const navLinks = [
  { name: "Inicio", href: "/" },
  {
    name: "Servicios",
    href: "#",
    children: [
      { name: "Asistente IA", href: "/asistente-ia", icon: Bot, description: "Tu contador virtual 24/7" },
      { name: "RESICO", href: "/resico", icon: FileText, description: "Régimen simplificado" },
      { name: "Actividad Empresarial", href: "/actividad-empresarial", icon: Users, description: "Para negocios" },
      { name: "Arrendamiento", href: "/arrendamiento", icon: Home, description: "Renta de inmuebles" },
      { name: "Plataformas Tecnológicas", href: "/plataformas-tecnologicas", icon: Smartphone, description: "Uber, Rappi, Airbnb" },
    ],
  },
  { name: "Empresas 🏆", href: "/empresas", badge: "NUEVO" },
  { name: "Precios", href: "/precios" },
  {
    name: "Recursos",
    href: "#",
    children: [
      { name: "Blog", href: "/blog", icon: BookOpen, description: "Artículos y guías" },
      { name: "Herramientas", href: "/herramientas", icon: Calculator, description: "Calculadoras gratis" },
      { name: "Glosario", href: "/glosario", icon: FileText, description: "Términos fiscales" },
    ],
  },
  { name: "Contacto", href: "/contacto" },
];

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-card/90 backdrop-blur-xl shadow-lg border-b border-border/50"
          : "bg-transparent"
      }`}
    >
      <nav className="container-custom">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img 
              src={logoSinconta} 
              alt="SinConta Logo" 
              className="h-10 w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.name} className="relative">
                {link.children ? (
                  <button
                    onMouseEnter={() => setOpenDropdown(link.name)}
                    onMouseLeave={() => setOpenDropdown(null)}
                    className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
                  >
                    {link.name}
                    <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === link.name ? "rotate-180" : ""}`} />
                  </button>
                ) : (
                  <Link
                    to={link.href}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-muted/50 ${
                      location.pathname === link.href
                        ? "text-secondary"
                        : "text-foreground/80 hover:text-foreground"
                    }`}
                  >
                    {link.name}
                    {link.badge && (
                      <Badge variant="secondary" className="bg-accent text-white text-[10px] px-1.5 py-0">
                        {link.badge}
                      </Badge>
                    )}
                  </Link>
                )}

                {/* Dropdown */}
                <AnimatePresence>
                  {link.children && openDropdown === link.name && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      onMouseEnter={() => setOpenDropdown(link.name)}
                      onMouseLeave={() => setOpenDropdown(null)}
                      className="absolute top-full left-0 pt-2"
                    >
                      <div className="glass-card p-2 min-w-[280px]">
                        {link.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            className="flex items-start gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors group"
                          >
                            <div className="p-2 rounded-lg bg-secondary/10 text-secondary group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors">
                              <child.icon className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{child.name}</p>
                              <p className="text-sm text-muted-foreground">{child.description}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Button variant="ghost" className="text-foreground/80 hover:text-foreground" asChild>
              <a href="https://app.taxo.co/login">Iniciar sesión</a>
            </Button>
            <Button className="btn-primary" asChild>
              <Link to="/registro">Contrata Ahora</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-muted/50 transition-colors"
            aria-label={isMobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-card/95 backdrop-blur-xl border-t border-border/50"
          >
            <div className="container-custom py-4 space-y-2">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.children ? (
                    <div>
                      <button
                        onClick={() => setOpenDropdown(openDropdown === link.name ? null : link.name)}
                        className="flex items-center justify-between w-full px-4 py-3 text-foreground font-medium rounded-lg hover:bg-muted/50"
                      >
                        {link.name}
                        <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === link.name ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {openDropdown === link.name && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="pl-4 space-y-1"
                          >
                            {link.children.map((child) => (
                              <Link
                                key={child.name}
                                to={child.href}
                                className="flex items-center gap-3 px-4 py-2 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted/50"
                              >
                                <child.icon className="w-4 h-4" />
                                {child.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={link.href}
                      className={`block px-4 py-3 font-medium rounded-lg hover:bg-muted/50 ${
                        location.pathname === link.href ? "text-secondary" : "text-foreground"
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t border-border/50 space-y-2">
                <Button variant="outline" className="w-full" asChild>
                  <a href="https://app.taxo.co/login">Iniciar sesión</a>
                </Button>
                <Button className="w-full btn-primary" asChild>
                  <Link to="/registro">Contrata Ahora</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
